import { InjectionToken, ɵɵdefineInjectable, ɵɵinject, Injectable, Inject, Optional, NgModule } from '@angular/core';
import { take, map, filter } from 'rxjs/operators';
import { HttpHeaders, HttpClient } from '@angular/common/http';
import mixpanel from 'mixpanel-browser';
import { NavigationEnd, Router } from '@angular/router';
const USER_DETAILS = new InjectionToken('USER_DETAILS');
const APP_SETTINGS = new InjectionToken('APP_SETTINGS');
class MixpanelEvent {
  constructor(telemetryEvent) {
    const mapping = {
      eventID: 'EventID',
      eventTitle: 'EventTitle',
      environment: 'Environment',
      time: 'Time',
      url: 'Url',
      CTNumber: 'CTNumber',
      id: 'UserID',
      HoldingID: 'HoldingID',
      organizationID: 'OrganizationID',
      reportID: 'ReportID',
      investingEntityID: 'InvestingEntityID',
      contactID: 'ContactID',
      additional: 'Additional',
      amountGroup: 'AmountGroup',
      sourcePage: 'SourcePage',
      authType: 'AuthType',
      entityType: 'EntityType',
      SOFLevel: 'SOFLevel',
      click: 'Click'
    };
    Object.keys(telemetryEvent).forEach(key => this[mapping[key] || key.replace(/(\w)(\w*)/g, function (g0, g1, g2) {
      return g1.toUpperCase() + g2;
    })] = telemetryEvent[key]);
  }
}
let MixpanelService = /*#__PURE__*/(() => {
  class MixpanelService {
    constructor(appSettings, userDetails) {
      this.appSettings = appSettings;
      this.userDetails = userDetails;
      this.url = this.appSettings.telemetryUrl;
      mixpanel.init(this.appSettings.mixpanelToken);
      this.userDetails.accountDetails$.pipe(take(1), map(details => {
        mixpanel.people.set_once({
          UserID: details.id
        });
        mixpanel.identify(details.id.toString());
      })).subscribe();
    }
    sendMixpanelEvent(event) {
      if (event.environment === 'PROD') {
        const mixpanelEvent = new MixpanelEvent(event);
        mixpanel.track(mixpanelEvent.EventTitle, mixpanelEvent);
      }
    }
  }
  MixpanelService.ɵprov = ɵɵdefineInjectable({
    factory: function MixpanelService_Factory() {
      return new MixpanelService(ɵɵinject(APP_SETTINGS), ɵɵinject(USER_DETAILS));
    },
    token: MixpanelService,
    providedIn: "root"
  });
  return MixpanelService;
})();
var AnalyticsServiceNameModel = /*#__PURE__*/function (AnalyticsServiceNameModel) {
  AnalyticsServiceNameModel[AnalyticsServiceNameModel["Mixpanel"] = 1] = "Mixpanel";
  AnalyticsServiceNameModel[AnalyticsServiceNameModel["Insights"] = 2] = "Insights";
  return AnalyticsServiceNameModel;
}(AnalyticsServiceNameModel || {});
let PreviousRouteService = /*#__PURE__*/(() => {
  class PreviousRouteService {
    constructor(router) {
      this.router = router;
      this.currentUrl = this.router.url;
      this.previousUrl = null;
      this.router.events.pipe(filter(event => event instanceof NavigationEnd)).subscribe(event => {
        this.previousUrl = this.currentUrl;
        this.currentUrl = event.urlAfterRedirects;
      });
    }
    getPreviousUrl() {
      return this.previousUrl;
    }
  }
  PreviousRouteService.ɵprov = ɵɵdefineInjectable({
    factory: function PreviousRouteService_Factory() {
      return new PreviousRouteService(ɵɵinject(Router));
    },
    token: PreviousRouteService,
    providedIn: "root"
  });
  return PreviousRouteService;
})();
let TelemetryService = /*#__PURE__*/(() => {
  class TelemetryService {
    constructor(userDetails, appSettings, http, mixpanelService, previousRoute) {
      this.userDetails = userDetails;
      this.appSettings = appSettings;
      this.http = http;
      this.mixpanelService = mixpanelService;
      this.previousRoute = previousRoute;
      this.url = this.appSettings.telemetryUrl;
    }
    create(event, serviceName) {
      event = Object.assign(Object.assign({}, event), this.getEventBasicData());
      this.userDetails.isLoggedIn$.pipe(take(1)).subscribe(isLoggedIn => {
        if (isLoggedIn) {
          this.createEventWithClientDetails(event, serviceName);
        } else {
          this.sendTelemetryEvent(event, serviceName);
        }
      });
    }
    createEventWithClientDetails(event, serviceName) {
      this.userDetails.accountDetails$.pipe(take(1)).subscribe(details => {
        event = Object.assign(Object.assign({}, event), {
          id: details.id,
          CTNumber: details.clientIdentifier,
          entityType: details.organizationDetails ? 'Organization' : 'Individual'
        });
        this.sendTelemetryEvent(event, serviceName);
      });
    }
    getEventBasicData() {
      return {
        environment: this.getRequestEnvironment(),
        time: Date.now(),
        url: window.location.href,
        sourcePage: this.previousRoute.getPreviousUrl()
      };
    }
    sendTelemetryEvent(data, serviceName) {
      switch (serviceName) {
        case AnalyticsServiceNameModel.Mixpanel:
          this.mixpanelService.sendMixpanelEvent(data);
          break;
        case AnalyticsServiceNameModel.Insights:
          this.sendInsightsEvent(data);
          break;
        case AnalyticsServiceNameModel.Mixpanel | AnalyticsServiceNameModel.Insights:
          this.mixpanelService.sendMixpanelEvent(data);
          this.sendInsightsEvent(data);
          break;
      }
    }
    sendInsightsEvent(data) {
      const headers = new HttpHeaders();
      headers.append('Content-Type', 'application/json');
      const requestBody = data ? JSON.stringify(data) : null;
      if (data.environment !== 'LOCAL' || this.appSettings.debugTelemetry) {
        this.http.post(this.url, requestBody, {
          headers,
          responseType: 'text'
        }).subscribe(() => {}, error => {
          console.error(error);
        });
      }
    }
    getRequestEnvironment() {
      const url = window.location.href;
      let environment;
      if (url.includes('localhost')) {
        environment = 'LOCAL';
      } else if (url.includes('testing')) {
        environment = 'TESTING';
      } else if (url.includes('automation')) {
        environment = 'AUTOMATION';
      } else if (url.includes('gp8200') || url.includes('lp6200') || url.includes('pay4200') || url.includes('staging')) {
        environment = 'STAGING';
      } else {
        environment = 'PROD';
      }
      return environment;
    }
  }
  TelemetryService.ɵprov = ɵɵdefineInjectable({
    factory: function TelemetryService_Factory() {
      return new TelemetryService(ɵɵinject(USER_DETAILS, 8), ɵɵinject(APP_SETTINGS, 8), ɵɵinject(HttpClient), ɵɵinject(MixpanelService), ɵɵinject(PreviousRouteService));
    },
    token: TelemetryService,
    providedIn: "root"
  });
  return TelemetryService;
})();
class TelemetryLibraryModule {}
/*
 * Public API Surface of telemetry-library
 */

/**
 * Generated bundle index. Do not edit.
 */

export { APP_SETTINGS, AnalyticsServiceNameModel, TelemetryLibraryModule, TelemetryService, USER_DETAILS, MixpanelService as ɵc, PreviousRouteService as ɵd };
